/**
 * Cloudinary video delivery — CDN-hosted clips with capped width and auto quality.
 * @see https://cloudinary.com/documentation/video_manipulation_and_delivery
 */

export const CLOUDINARY_CLOUD_NAME =
  process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'deelyxcjk';

/** Folder in Cloudinary Media Library (public IDs match your uploaded assets). */
export const CLOUDINARY_VIDEO_FOLDER =
  process.env.REACT_APP_CLOUDINARY_VIDEO_FOLDER || 'databiqs-assets';

const CLOUDINARY_VIDEO_BASE = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/video/upload`;
const CLOUDINARY_IMAGE_BASE = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload`;

/** Team portraits — limit width; avoid aggressive crop so faces stay positioned correctly. */
export const CLOUDINARY_IMAGE_PRESETS = {
  team: 'f_auto,q_auto:good,w_700,c_limit',
  teamSpotlight: 'f_auto,q_auto:good,w_900,c_limit',
  /** Founder/CEO — remove baked-in studio/checkerboard bg, deliver PNG with alpha. */
  ceoPortrait: 'e_background_removal,f_png,q_auto:good,w_900,c_limit',
  service: 'f_auto,q_auto:good,w_1200,c_limit',
  /** SVG illustrations — keep vector quality, light compression only. */
  illustration: 'f_auto,q_auto:best',
};

/** Smaller width + eco quality for cards; higher for hero / solution tabs. */
export const CLOUDINARY_VIDEO_PRESETS = {
  card: 'f_mp4,q_auto:eco,w_1280,c_limit',
  solution: 'f_mp4,q_auto:eco,w_960,c_limit',
  hero: 'f_mp4,q_auto:good,w_1920,c_limit',
};

/**
 * @param {{ version: string, publicId: string }} asset
 * @param {'card' | 'solution' | 'hero'} [preset]
 */
export function buildCloudinaryVideoUrl(asset, preset = 'card') {
  const transforms =
    CLOUDINARY_VIDEO_PRESETS[preset] || CLOUDINARY_VIDEO_PRESETS.card;
  const id = asset.publicId.endsWith('.mp4')
    ? asset.publicId.slice(0, -4)
    : asset.publicId;
  return `${CLOUDINARY_VIDEO_BASE}/${transforms}/${asset.version}/${id}.mp4`;
}

/**
 * @param {{ version: string, publicId: string, format?: string }} asset
 * @param {'team' | 'teamSpotlight' | 'ceoPortrait' | 'service' | 'illustration'} [preset]
 */
export function buildCloudinaryImageUrl(asset, preset = 'team') {
  const transforms =
    CLOUDINARY_IMAGE_PRESETS[preset] || CLOUDINARY_IMAGE_PRESETS.team;
  const id = asset.publicId.replace(/\.(png|jpe?g|webp|jfif)$/i, '');
  const format = preset === 'ceoPortrait' ? 'png' : asset.format || 'png';
  return `${CLOUDINARY_IMAGE_BASE}/${transforms}/${asset.version}/${id}.${format}`;
}

export function isVideoUrl(src) {
  if (typeof src !== 'string' || !src) return false;
  const lower = src.toLowerCase();
  return (
    lower.includes('.mp4') ||
    (lower.includes('res.cloudinary.com') && lower.includes('/video/'))
  );
}
